import React, { useEffect, useState } from 'react'
import {
  Backdrop,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  InputAdornment,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { Add, CardMembership, Search } from '@mui/icons-material'
import { Helmet } from 'react-helmet'
import { useNavigate } from 'react-router-dom'
import { api } from '../../../../services/Main/Api'
import PaginationGlobal from '../../../../components/Shared/PaginationGlobal'
import NoResultsAnimation from '../../../../components/Shared/animations/NoResultsAnimation'

const PlanList = () => {
  const navigate = useNavigate()

  const [response, setResponse] = useState(null)
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState({ page: 1, page_size: 50 })

  const fetchData = async () => {
    setLoading(true)
    try {
      const params = {
        page: filters.page,
        page_size: filters.page_size,
        ...(search && { search }),
      }
      const res = await api.get('/plan/plans/', { params })
      setResponse(res.data)
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [filters])

  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters(prev => ({ ...prev, page: 1 }))
    }, 1000)
    return () => clearTimeout(timer)
  }, [search])

  return (
    <>
      <Helmet>
        <title>Planos - Admin</title>
      </Helmet>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" fontWeight="bold">
            Planos
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/admin/planos/create')}
          >
            Novo Plano
          </Button>
        </Box>

        <Card>
          <Box sx={{ p: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Pesquisar por nome..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {response === null ? (
            <Skeleton variant="rounded" height={400} animation="pulse" sx={{ m: 2 }} />
          ) : response?.results?.length === 0 ? (
            <Box sx={{ p: 4 }}>
              <NoResultsAnimation />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Nome</TableCell>
                      <TableCell>Slug</TableCell>
                      <TableCell>Funcionalidades</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Ordem</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {response?.results?.map((plan) => (
                      <TableRow
                        key={plan.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/admin/planos/${plan.id}`)}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CardMembership fontSize="small" color="primary" />
                            {plan.name}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                            {plan.slug}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={`${plan.features?.length || 0} features`}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={plan.is_active ? 'Ativo' : 'Inativo'}
                            size="small"
                            color={plan.is_active ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell>{plan.order}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {response?.count > 0 && (
                <PaginationGlobal
                  state={response}
                  filters={filters}
                  setFilters={setFilters}
                />
              )}
            </>
          )}
        </Card>
      </Container>
    </>
  )
}

export default PlanList
